import { reactive, ref } from "vue";
import dagre from "@dagrejs/dagre";
import type { FlowDefinition, FlowNode } from "../api/flows";
import { t } from "../i18n";
import { ruleLabel } from "./flowConditions";

/**
 * Editing model for the builder: the definition is the truth; canvas nodes and edges are derived.
 * Exits describe where each node can jump to and which field holds the target.
 */
export interface Exit {
  handle: string;
  label: string;
  /** Left unconnected on purpose: the block does without it (a wait that ignores replies). */
  optional?: boolean;
  get: (n: FlowNode) => string | undefined;
  set: (n: FlowNode, target: string | undefined) => void;
}

const simple =
  (field: string, label: () => string): ((n: FlowNode) => Exit[]) =>
  () => [
    {
      handle: field,
      label: label(),
      get: (n) => n[field] as string | undefined,
      set: (n, v) => (v ? (n[field] = v) : delete n[field]),
    },
  ];

export const KINDS = [
  "start",
  "message",
  "template",
  "menu",
  "input",
  "ai_route",
  "condition",
  "ai_condition",
  "http_request",
  "script",
  "set_variable",
  "set_field",
  "tag",
  "ai_tags",
  "ai_fields",
  "optout",
  "split",
  "repeat",
  "wait",
  "business_hours",
  "goto_flow",
  "transfer",
  "assistant",
  "schedule",
  "close",
  "event",
  "exception",
  "end",
] as const;
export type Kind = (typeof KINDS)[number];

export const KIND_META: Record<
  Kind,
  { icon: string; color: string; terminal?: boolean }
> = {
  start: { icon: "play", color: "var(--wa-green)" },
  message: { icon: "chat", color: "var(--wa-info)" },
  template: { icon: "file", color: "var(--wa-info)" },
  menu: { icon: "list", color: "var(--wa-info)" },
  input: { icon: "edit", color: "var(--wa-info)" },
  condition: { icon: "grip", color: "var(--wa-pending)" },
  http_request: { icon: "link", color: "var(--wa-pending)" },
  script: { icon: "code", color: "var(--wa-pending)" },
  set_variable: { icon: "code", color: "var(--wa-muted)" },
  set_field: { icon: "user", color: "var(--wa-muted)" },
  tag: { icon: "star", color: "var(--wa-muted)" },
  optout: { icon: "mute", color: "var(--wa-muted)" },
  ai_route: { icon: "sparkles", color: "#7c3aed" },
  ai_condition: { icon: "sparkles", color: "#7c3aed" },
  ai_tags: { icon: "sparkles", color: "#7c3aed" },
  ai_fields: { icon: "sparkles", color: "#7c3aed" },
  split: { icon: "split", color: "var(--wa-pending)" },
  repeat: { icon: "repeat", color: "var(--wa-pending)" },
  goto_flow: { icon: "workflow", color: "var(--wa-green)", terminal: true },
  wait: { icon: "clock", color: "var(--wa-muted)" },
  business_hours: { icon: "calls", color: "var(--wa-pending)" },
  transfer: { icon: "forward", color: "var(--wa-green)", terminal: true },
  assistant: { icon: "sparkles", color: "#7c3aed" },
  schedule: { icon: "calendar", color: "var(--wa-green)" },
  close: { icon: "checkCircle", color: "var(--wa-green)", terminal: true },
  event: { icon: "bell", color: "var(--wa-muted)" },
  exception: { icon: "xCircle", color: "var(--wa-danger)" },
  end: { icon: "stop", color: "var(--wa-subtle)", terminal: true },
};

/** Blocks that only work with something chosen, and where it is chosen in the inspector. */
const REQUIRED_CHOICE: Partial<Record<Kind, { field: string; label: string }>> =
  {
    assistant: { field: "assistantId", label: "node.field.assistant" },
    template: { field: "templateId", label: "node.field.template" },
    schedule: { field: "typeId", label: "node.schedule.type" },
    goto_flow: { field: "flowId", label: "node.field.flow" },
  };

/**
 * The first block still missing its choice (an assistant without the assistant, a template
 * without the template…), so saving can point at it instead of failing on the server.
 */
export function missingChoice(
  def: FlowDefinition,
): { node: FlowNode; block: string; field: string } | null {
  for (const node of def.nodes) {
    // A tag block adds or removes at least one tag.
    if (
      node.kind === "tag" &&
      !node.tagId &&
      !(node.add as string[] | undefined)?.length &&
      !(node.remove as string[] | undefined)?.length
    )
      return {
        node,
        block: t("node.tag"),
        field: t("node.field.tag"),
      };
    if (
      node.kind === "ai_fields" &&
      !(node.fields as string[] | undefined)?.length
    )
      return {
        node,
        block: t("node.ai_fields"),
        field: t("node.ai_fields.fields"),
      };
    if (node.kind === "ai_condition" && !String(node.question ?? "").trim())
      return {
        node,
        block: t("node.ai_condition"),
        field: t("node.ai_condition.question"),
      };
    const need = REQUIRED_CHOICE[node.kind as Kind];
    if (need && !(node as Record<string, unknown>)[need.field])
      return {
        node,
        block: t(("node." + node.kind) as never),
        field: t(need.label as never),
      };
  }
  return null;
}

export function exitsOf(n: FlowNode): Exit[] {
  switch (n.kind as Kind) {
    case "start":
    case "message":
    case "set_variable":
    case "set_field":
    case "tag":
    case "ai_tags":
    case "ai_fields":
    case "optout":
    case "event":
    case "exception":
      return simple("next", () => t("builder.exit_next"))(n);
    case "split": {
      const paths =
        (n.paths as Array<{ id: string; weight: number; next?: string }>) ?? [];
      const total = paths.reduce((sum, p) => sum + (Number(p.weight) || 0), 0);
      return paths.map((p, i) => ({
        handle: `path:${p.id}`,
        label: t("builder.exit_path", {
          n: i + 1,
          pct: total ? Math.round(((Number(p.weight) || 0) / total) * 100) : 0,
        }),
        get: () => paths[i]?.next,
        set: (_n: FlowNode, v: string | undefined) =>
          v ? (paths[i]!.next = v) : delete paths[i]!.next,
      }));
    }
    case "ai_route": {
      const routes =
        (n.routes as Array<{ id: string; label: string; next?: string }>) ?? [];
      return [
        ...routes.map((r, i) => ({
          handle: `route:${r.id}`,
          label: r.label || `#${i + 1}`,
          get: () => routes[i]?.next,
          set: (_n: FlowNode, v: string | undefined) =>
            v ? (routes[i]!.next = v) : delete routes[i]!.next,
        })),
        {
          handle: "fallback",
          label: t("builder.exit_ai_unsure"),
          get: () => n.fallbackNext as string | undefined,
          set: (_n, v) => (v ? (n.fallbackNext = v) : delete n.fallbackNext),
        },
        ...timeoutExit(n),
      ];
    }
    case "ai_condition":
      return [
        {
          handle: "yes",
          label: t("builder.exit_yes"),
          get: () => n.yesNext as string | undefined,
          set: (_n, v) => (v ? (n.yesNext = v) : delete n.yesNext),
        },
        {
          handle: "no",
          label: t("builder.exit_no"),
          get: () => n.noNext as string | undefined,
          set: (_n, v) => (v ? (n.noNext = v) : delete n.noNext),
        },
      ];
    case "repeat":
      return [
        {
          handle: "loop",
          label: t("builder.exit_repeat_loop", { n: Number(n.times ?? 1) }),
          get: () => n.loopNext as string | undefined,
          set: (_n, v) => (v ? (n.loopNext = v) : delete n.loopNext),
        },
        {
          handle: "done",
          label: t("builder.exit_repeat_done"),
          get: () => n.doneNext as string | undefined,
          set: (_n, v) => (v ? (n.doneNext = v) : delete n.doneNext),
        },
      ];
    case "wait":
      // A date read from the conversation may not be a date: that exit only exists for it.
      return [
        {
          handle: "next",
          label: t("builder.exit_next"),
          get: () => n.next as string | undefined,
          set: (_n, v) => (v ? (n.next = v) : delete n.next),
        },
        {
          handle: "reply",
          optional: true,
          label: t("builder.exit_reply"),
          get: () => n.replyNext as string | undefined,
          set: (_n, v) => (v ? (n.replyNext = v) : delete n.replyNext),
        },
        ...(n.until !== undefined
          ? [
              {
                handle: "invalid",
                label: t("builder.exit_invalid_date"),
                get: () => n.invalidNext as string | undefined,
                set: (_n: FlowNode, v: string | undefined) =>
                  v ? (n.invalidNext = v) : delete n.invalidNext,
              },
            ]
          : []),
      ];
    case "menu": {
      const options =
        (n.options as Array<{ id: string; title: string; next?: string }>) ??
        [];
      return [
        ...options.map((o, i) => ({
          handle: `opt:${o.id}`,
          label: o.title || `#${i + 1}`,
          get: () => options[i]?.next,
          set: (_n: FlowNode, v: string | undefined) =>
            v ? (options[i]!.next = v) : delete options[i]!.next,
        })),
        {
          handle: "fallback",
          label: t("builder.exit_fallback"),
          get: () => n.fallbackNext as string | undefined,
          set: (_n, v) => (v ? (n.fallbackNext = v) : delete n.fallbackNext),
        },
        ...timeoutExit(n),
      ];
    }
    case "input":
      return [
        {
          handle: "next",
          label: t("builder.exit_next"),
          get: () => n.next as string | undefined,
          set: (_n, v) => (v ? (n.next = v) : delete n.next),
        },
        {
          handle: "invalid",
          label: t("builder.exit_invalid"),
          get: () => n.invalidNext as string | undefined,
          set: (_n, v) => (v ? (n.invalidNext = v) : delete n.invalidNext),
        },
        ...timeoutExit(n),
      ];
    case "condition": {
      const rules =
        (n.rules as Array<{
          variable: string;
          op: string;
          value?: unknown;
          next?: string;
        }>) ?? [];
      return [
        ...rules.map((r, i) => ({
          handle: `rule:${i}`,
          label: ruleLabel(r, entityNames),
          get: () => rules[i]?.next,
          set: (_n: FlowNode, v: string | undefined) =>
            v ? (rules[i]!.next = v) : delete rules[i]!.next,
        })),
        {
          handle: "default",
          label: t("builder.exit_default"),
          get: () => n.defaultNext as string | undefined,
          set: (_n, v) => (v ? (n.defaultNext = v) : delete n.defaultNext),
        },
      ];
    }
    case "template": {
      // Waiting for the reply: one path per button (and another reply), or "Respondeu".
      const buttons =
        (n.buttons as Array<{ text: string; next?: string }> | undefined) ?? [];
      const waits = Boolean(n.waitReply) || buttons.length > 0;
      return [
        ...buttons.map((b, i) => ({
          handle: `button:${i}`,
          label: b.text,
          get: () => buttons[i]?.next,
          set: (_n: FlowNode, v: string | undefined) =>
            v ? (buttons[i]!.next = v) : delete buttons[i]!.next,
        })),
        ...(buttons.length
          ? [
              {
                handle: "other",
                label: t("builder.exit_other_reply"),
                get: () => n.noMatchNext as string | undefined,
                set: (_n: FlowNode, v: string | undefined) =>
                  v ? (n.noMatchNext = v) : delete n.noMatchNext,
              },
            ]
          : [
              {
                handle: "next",
                label: waits
                  ? t("builder.exit_replied")
                  : t("builder.exit_next"),
                get: () => n.next as string | undefined,
                set: (_n: FlowNode, v: string | undefined) =>
                  v ? (n.next = v) : delete n.next,
              },
            ]),
        {
          // The contact paused or refused active messages (decision 0036).
          handle: "held",
          label: t("builder.exit_held"),
          get: () => n.heldNext as string | undefined,
          set: (_n, v) => (v ? (n.heldNext = v) : delete n.heldNext),
        },
        {
          handle: "error",
          label: t("builder.exit_error"),
          get: () => n.errorNext as string | undefined,
          set: (_n, v) => (v ? (n.errorNext = v) : delete n.errorNext),
        },
        ...(waits ? timeoutExit(n) : []),
      ];
    }
    case "http_request":
    case "script":
      return [
        {
          handle: "next",
          label: t("builder.exit_next"),
          get: () => n.next as string | undefined,
          set: (_n, v) => (v ? (n.next = v) : delete n.next),
        },
        {
          handle: "error",
          label: t("builder.exit_error"),
          get: () => n.errorNext as string | undefined,
          set: (_n, v) => (v ? (n.errorNext = v) : delete n.errorNext),
        },
      ];
    case "business_hours":
      return [
        {
          handle: "inside",
          label: t("builder.exit_inside"),
          get: () => n.insideNext as string | undefined,
          set: (_n, v) => (v ? (n.insideNext = v) : delete n.insideNext),
        },
        {
          handle: "outside",
          label: t("builder.exit_outside"),
          get: () => n.outsideNext as string | undefined,
          set: (_n, v) => (v ? (n.outsideNext = v) : delete n.outsideNext),
        },
      ];
    case "schedule":
      return [
        {
          handle: "next",
          label: t("builder.exit_booked"),
          get: () => n.next as string | undefined,
          set: (_n, v) => (v ? (n.next = v) : delete n.next),
        },
        {
          handle: "declined",
          label: t("builder.exit_declined"),
          get: () => n.declinedNext as string | undefined,
          set: (_n, v) => (v ? (n.declinedNext = v) : delete n.declinedNext),
        },
        {
          handle: "none",
          label: t("builder.exit_no_times"),
          get: () => n.noTimesNext as string | undefined,
          set: (_n, v) => (v ? (n.noTimesNext = v) : delete n.noTimesNext),
        },
      ];
    case "assistant":
      return [
        {
          handle: "exit",
          label: t("builder.exit_done"),
          get: () => n.exitNext as string | undefined,
          set: (_n, v) => (v ? (n.exitNext = v) : delete n.exitNext),
        },
      ];
    default:
      return [];
  }
}

/** A menu's or a question's own "N min sem resposta" exit, when it has its own wait. */
function timeoutExit(n: FlowNode): Exit[] {
  const minutes = Number(n.timeoutMinutes ?? 0);
  if (!minutes) return [];
  return [
    {
      handle: "timeout",
      label: t("builder.exit_timeout", { time: minutesText(minutes) }),
      get: () => n.timeoutNext as string | undefined,
      set: (_n, v) => (v ? (n.timeoutNext = v) : delete n.timeoutNext),
    },
  ];
}

/** "30 min", "2 h", "1 dia": a wait in the unit it was set in. */
export function minutesText(minutes: number): string {
  return minutes % 1440 === 0
    ? t("builder.wait_days", { n: minutes / 1440 })
    : minutes % 60 === 0
      ? t("builder.wait_hours", { n: minutes / 60 })
      : t("builder.wait_minutes", { n: minutes });
}

/** The next free id for a node of this kind: menu-3, transfer-7… */
export function nextNodeId(kind: string, existing: FlowNode[]): string {
  const base = kind.replace("_", "-");
  let i = existing.filter((n) => n.kind === kind).length + 1;
  let id = `${base}-${i}`;
  while (existing.some((n) => n.id === id)) id = `${base}-${++i}`;
  return id;
}

export function newNode(kind: Kind, existing: FlowNode[]): FlowNode {
  const id = nextNodeId(kind, existing);
  switch (kind) {
    case "message":
      return { id, kind, text: "" };
    case "template":
      return { id, kind, templateId: "", bodyParams: [], buttonParams: [] };
    case "menu":
      return {
        id,
        kind,
        body: "",
        retries: 2,
        options: [
          { id: "opt-1", title: t("builder.default_option", { n: 1 }) },
          { id: "opt-2", title: t("builder.default_option", { n: 2 }) },
        ],
      };
    case "input":
      return {
        id,
        kind,
        prompt: "",
        variable: "resposta",
        validation: "text",
        retries: 2,
      };
    case "condition":
      return {
        id,
        kind,
        rules: [{ variable: "vars.resposta", op: "eq", value: "" }],
      };
    case "http_request":
      return {
        id,
        kind,
        method: "GET",
        url: "https://",
        headers: {},
        variable: "api",
        timeoutMs: 10000,
      };
    case "script":
      return {
        id,
        kind,
        code: t("builder.script_default"),
      };
    case "set_variable":
      return { id, kind, variable: "nome", value: "" };
    case "set_field":
      return { id, kind, field: "campo", value: "" };
    case "tag":
      return { id, kind, add: [], remove: [], target: "contact" };
    case "optout":
      return { id, kind, receive: false };
    case "ai_route":
      return {
        id,
        kind,
        prompt: "",
        routes: [
          { id: "r1", label: t("builder.default_route", { n: 1 }) },
          { id: "r2", label: t("builder.default_route", { n: 2 }) },
        ],
      };
    case "ai_condition":
      return { id, kind, question: "" };
    case "ai_tags":
      return { id, kind, tagIds: [] };
    case "ai_fields":
      return { id, kind, fields: [] };
    case "split":
      return {
        id,
        kind,
        paths: [
          { id: "a", weight: 50 },
          { id: "b", weight: 50 },
        ],
      };
    case "repeat":
      return { id, kind, times: 1 };
    case "goto_flow":
      return { id, kind, flowId: "" };
    case "wait":
      return { id, kind, seconds: 3600, timeZone: "America/Sao_Paulo" };
    case "business_hours":
      return { id, kind, insideNext: "", outsideNext: "" };
    case "transfer":
      return { id, kind };
    case "assistant":
      return { id, kind, assistantId: "" };
    case "schedule":
      return {
        id,
        kind,
        typeId: "",
        mode: "list",
        variable: "appointment_id",
        answers: {},
        retries: 2,
      };
    case "close":
      return { id, kind, outcomeCode: "resolved" };
    case "event":
      return { id, kind, name: "evento", data: {} };
    case "exception":
      return { id, kind, text: "" };
    case "end":
      return { id, kind };
    default:
      return { id, kind };
  }
}

/** Short text for the node card. */
export function summarize(n: FlowNode): string {
  const s = (v: unknown) => (typeof v === "string" ? v : "");
  switch (n.kind as Kind) {
    case "message": {
      const file = n.media as { filename?: string; kind?: string } | undefined;
      const text = n.vary ? `✨ ${s(n.text)}` : s(n.text);
      return file
        ? `📎 ${file.filename ?? file.kind ?? ""}${text ? ` · ${text}` : ""}`
        : text;
    }
    case "exception":
      return s(n.text);
    case "menu":
      return s(n.body);
    case "input":
      return `${s(n.prompt)} → {{vars.${s(n.variable)}}}`;
    case "condition":
      return `${((n.rules as unknown[]) ?? []).length} ${t("node.field.rules").toLowerCase()}`;
    case "http_request":
      return `${s(n.method)} ${s(n.url)}`;
    case "script":
      return (
        s(n.code)
          .split("\n")
          .find((l) => l.trim() && !l.trim().startsWith("//"))
          ?.trim() ?? ""
      );
    case "set_variable":
      return `${s(n.variable)} = ${s(n.value)}`;
    case "set_field":
      return `${s(n.field)} = ${s(n.value)}`;
    case "wait":
      return waitSummary(n);
    case "template":
      return templateNames[s(n.templateId)] ?? "";
    case "schedule":
      return [
        appointmentTypeNames[s(n.typeId)] ?? "",
        t(`node.schedule.mode.${s(n.mode) || "list"}` as never),
      ]
        .filter(Boolean)
        .join(" · ");
    case "close":
      return s(n.outcomeCode);
    case "event":
      return s(n.name);
    case "tag": {
      const names = (ids: unknown) =>
        ((ids as string[] | undefined) ?? [])
          .map((id) => entityNames.tags[id] ?? "")
          .filter(Boolean)
          .join(", ");
      const add = names([
        ...(n.tagId ? [n.tagId] : []),
        ...((n.add as string[]) ?? []),
      ]);
      const remove = names(n.remove);
      return [add && `+ ${add}`, remove && `− ${remove}`]
        .filter(Boolean)
        .join(" · ");
    }
    case "optout":
      return n.receive ? t("node.optout.on") : t("node.optout.off");
    case "ai_route":
      return s(n.prompt) || t("node.ai_route.reads_message");
    case "ai_condition":
      return s(n.question);
    case "ai_tags": {
      const ids = (n.tagIds as string[] | undefined) ?? [];
      return ids.length
        ? ids
            .map((id) => entityNames.tags[id] ?? "")
            .filter(Boolean)
            .join(", ")
        : t("node.ai_tags.any");
    }
    case "ai_fields":
      return ((n.fields as string[] | undefined) ?? [])
        .map((k) => entityNames.fields[k] ?? k)
        .join(", ");
    case "repeat":
      return t("builder.repeat_summary", { n: Number(n.times ?? 1) });
    case "goto_flow":
      return entityNames.flows[s(n.flowId)] ?? "";
    default:
      return "";
  }
}

/** Names the editor loads, for cards and exits that show a tag, a person or a flow by name. */
export const entityNames = reactive({
  tags: {} as Record<string, string>,
  members: {} as Record<string, string>,
  flows: {} as Record<string, string>,
  fields: {} as Record<string, string>,
});

/** Names of the workspace's templates, for the template node's card; the editor fills it. */
export const templateNames = reactive<Record<string, string>>({});
/** "Agenda · Tipo" of each appointment type, for the schedule node's card. */
export const appointmentTypeNames = reactive<Record<string, string>>({});

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

function waitSummary(n: FlowNode): string {
  if (typeof n.until === "string")
    return t("builder.wait_until_summary", { when: n.until });
  if (typeof n.at === "string") {
    const days = (n.weekdays as number[] | undefined) ?? [];
    return days.length && days.length < 7
      ? t("builder.wait_at_days_summary", {
          time: n.at,
          days: days
            .map((d) => t(`weekday.short.${WEEKDAY_KEYS[d]}` as never))
            .join(", "),
        })
      : t("builder.wait_at_summary", { time: n.at });
  }
  const sec = Number(n.seconds ?? 0);
  return sec % 86400 === 0
    ? t("builder.wait_days", { n: sec / 86400 })
    : sec % 3600 === 0
      ? t("builder.wait_hours", { n: sec / 3600 })
      : sec % 60 === 0
        ? t("builder.wait_minutes", { n: sec / 60 })
        : `${sec}s`;
}

/** Layer-based auto layout for definitions without saved positions. */
export function autoLayout(
  def: FlowDefinition,
): Record<string, { x: number; y: number }> {
  const layout: Record<string, { x: number; y: number }> = {
    ...(def.layout ?? {}),
  };
  const byId = new Map(def.nodes.map((n) => [n.id, n] as const));
  const depth = new Map<string, number>();
  const queue: Array<[string, number]> = [[def.start, 0]];
  while (queue.length) {
    const [id, d] = queue.shift()!;
    if (depth.has(id)) continue;
    depth.set(id, d);
    const n = byId.get(id);
    if (!n) continue;
    for (const e of exitsOf(n)) {
      const target = e.get(n);
      if (target && !depth.has(target)) queue.push([target, d + 1]);
    }
  }
  let orphan = 0;
  for (const n of def.nodes)
    if (!depth.has(n.id)) depth.set(n.id, 99 + orphan++);
  const perLayer = new Map<number, number>();
  for (const n of def.nodes) {
    if (layout[n.id]) continue;
    const d = depth.get(n.id) ?? 0;
    const i = perLayer.get(d) ?? 0;
    perLayer.set(d, i + 1);
    layout[n.id] = { x: 60 + d * 300, y: 60 + i * 150 };
  }
  return layout;
}

type Point = { x: number; y: number };
type Size = { width: number; height: number };

/**
 * "Organizar": the flow laid out left to right from its real node sizes, with few crossing lines
 * (dagre). With `only`, just those nodes move, and their group stays where it was on the canvas.
 */
/** Where a card's lines attach, measured on screen: the input and each exit, from the card's top. */
export type HandleOffsets = Map<
  string,
  { in?: number; out: Record<string, number> }
>;

/**
 * Cards sitting in the way of a line that skips their column move down until the line runs clear
 * of them; then the cards of each column are kept apart again. A few passes settle it.
 */
function clearLines(
  placed: Record<string, Point>,
  sizes: Map<string, Size>,
  lines: Array<{ from: string; handle: string; to: string }>,
  handles: HandleOffsets | undefined,
  gap: number,
): void {
  const size = (id: string) => sizes.get(id) ?? { width: 300, height: 120 };
  for (let pass = 0; pass < 4; pass++) {
    let moved = false;
    for (const l of lines) {
      const a = placed[l.from];
      const b = placed[l.to];
      if (!a || !b) continue;
      const x1 = a.x + size(l.from).width;
      const y1 =
        a.y + (handles?.get(l.from)?.out[l.handle] ?? size(l.from).height / 2);
      const x2 = b.x;
      const y2 = b.y + (handles?.get(l.to)?.in ?? size(l.to).height / 2);
      if (x2 - x1 < 1) continue;
      for (const [id, p] of Object.entries(placed)) {
        if (id === l.from || id === l.to) continue;
        const w = size(id).width;
        if (p.x < x1 || p.x + w > x2) continue;
        const yA = y1 + ((y2 - y1) * (p.x - x1)) / (x2 - x1);
        const yB = y1 + ((y2 - y1) * (p.x + w - x1)) / (x2 - x1);
        const top = Math.min(yA, yB) - gap;
        const bottom = Math.max(yA, yB) + gap;
        if (p.y < bottom && p.y + size(id).height > top) {
          p.y = bottom;
          moved = true;
        }
      }
    }
    const columns = new Map<number, string[]>();
    for (const [id, p] of Object.entries(placed)) {
      const key = Math.round(p.x + size(id).width / 2);
      columns.set(key, [...(columns.get(key) ?? []), id]);
    }
    for (const ids of columns.values()) {
      ids.sort((x, y) => placed[x]!.y - placed[y]!.y);
      for (let i = 1; i < ids.length; i++) {
        const prev = placed[ids[i - 1]!]!;
        const cur = placed[ids[i]!]!;
        const min = prev.y + size(ids[i - 1]!).height + gap;
        if (cur.y < min) {
          cur.y = min;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
}

export function arrangeLayout(
  def: FlowDefinition,
  sizes: Map<string, Size>,
  only?: Set<string>,
  handles?: HandleOffsets,
): Record<string, Point> {
  const ids = def.nodes.map((n) => n.id).filter((id) => !only || only.has(id));
  const inside = new Set(ids);
  const g = new dagre.graphlib.Graph();
  // Tight columns so the whole flow fits at a readable size; room between cards of a column so a
  // line skipping a column runs beside its cards, not behind them.
  g.setGraph({
    rankdir: "LR",
    nodesep: 56,
    edgesep: 40,
    ranksep: 64,
    marginx: 0,
    marginy: 0,
  });
  g.setDefaultEdgeLabel(() => ({}));
  for (const id of ids) {
    const size = sizes.get(id) ?? { width: 300, height: 120 };
    g.setNode(id, { width: size.width, height: size.height });
  }
  const lines: Array<{ from: string; handle: string; to: string }> = [];
  for (const n of def.nodes) {
    if (!inside.has(n.id)) continue;
    for (const e of exitsOf(n)) {
      const target = e.get(n);
      if (target && target !== n.id && inside.has(target)) {
        g.setEdge(n.id, target);
        lines.push({ from: n.id, handle: e.handle, to: target });
      }
    }
  }
  dagre.layout(g);
  const placed: Record<string, Point> = {};
  for (const id of ids) {
    const p = g.node(id) as {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    placed[id] = { x: p.x - p.width / 2, y: p.y - p.height / 2 };
  }
  clearLines(placed, sizes, lines, handles, 28);
  // The group's top-left corner: where the chosen nodes already were, or the canvas origin.
  const before = ids
    .map((id) => def.layout?.[id])
    .filter((p): p is Point => !!p);
  const origin =
    only && before.length
      ? {
          x: Math.min(...before.map((p) => p.x)),
          y: Math.min(...before.map((p) => p.y)),
        }
      : { x: 60, y: 60 };
  const minX = Math.min(...Object.values(placed).map((p) => p.x));
  const minY = Math.min(...Object.values(placed).map((p) => p.y));
  const out: Record<string, Point> = { ...(def.layout ?? {}) };
  for (const [id, p] of Object.entries(placed))
    out[id] = {
      x: Math.round(p.x - minX + origin.x),
      y: Math.round(p.y - minY + origin.y),
    };
  return out;
}

/**
 * Copies of these nodes (never the start), placed a little below and to the right. Links among
 * the copied nodes point to the copies; links to the rest of the flow stay as they were.
 */
export function duplicateNodes(def: FlowDefinition, ids: string[]): string[] {
  const renamed = new Map<string, string>();
  const copies: FlowNode[] = [];
  const all = [...def.nodes];
  for (const id of ids) {
    const n = def.nodes.find((x) => x.id === id);
    if (!n || id === def.start || n.kind === "start") continue;
    const copy = JSON.parse(JSON.stringify(n)) as FlowNode;
    copy.id = nextNodeId(n.kind, all);
    renamed.set(id, copy.id);
    copies.push(copy);
    all.push(copy);
  }
  for (const c of copies)
    for (const e of exitsOf(c)) {
      const target = e.get(c);
      if (target && renamed.has(target)) e.set(c, renamed.get(target));
    }
  def.nodes.push(...copies);
  def.layout ??= {};
  for (const [from, to] of renamed) {
    const p = def.layout[from] ?? { x: 60, y: 60 };
    def.layout[to] = { x: p.x + 48, y: p.y + 48 };
  }
  return [...renamed.values()];
}

/** Blocks copied with ⌘/Ctrl+C: themselves, their places, and the links among them. */
export interface FlowClip {
  nodes: FlowNode[];
  layout: Record<string, { x: number; y: number }>;
}

export function copyNodes(def: FlowDefinition, ids: string[]): FlowClip | null {
  const picked = def.nodes.filter(
    (n) => ids.includes(n.id) && n.kind !== "start",
  );
  if (!picked.length) return null;
  return {
    nodes: JSON.parse(JSON.stringify(picked)) as FlowNode[],
    layout: Object.fromEntries(
      picked.map((n) => [n.id, def.layout?.[n.id] ?? { x: 0, y: 0 }]),
    ),
  };
}

/**
 * Pastes copied blocks (from this flow or another) with new ids, keeping the links among them
 * and dropping the ones to blocks that did not come along. Placed with their top-left at `at`.
 */
export function pasteNodes(
  def: FlowDefinition,
  clip: FlowClip,
  at: { x: number; y: number },
): string[] {
  const renamed = new Map<string, string>();
  const all = [...def.nodes];
  const copies: FlowNode[] = [];
  for (const n of clip.nodes) {
    if (n.kind === "start" || !KINDS.includes(n.kind as Kind)) continue;
    const copy = JSON.parse(JSON.stringify(n)) as FlowNode;
    copy.id = nextNodeId(n.kind, all);
    renamed.set(n.id, copy.id);
    copies.push(copy);
    all.push(copy);
  }
  for (const c of copies)
    for (const e of exitsOf(c)) {
      const target = e.get(c);
      if (target) e.set(c, renamed.get(target));
    }
  const xs = clip.nodes.map((n) => clip.layout[n.id]?.x ?? 0);
  const ys = clip.nodes.map((n) => clip.layout[n.id]?.y ?? 0);
  const [minX, minY] = [Math.min(...xs), Math.min(...ys)];
  def.nodes.push(...copies);
  def.layout ??= {};
  for (const [from, to] of renamed) {
    const p = clip.layout[from] ?? { x: minX, y: minY };
    def.layout[to] = {
      x: Math.round(at.x + p.x - minX),
      y: Math.round(at.y + p.y - minY),
    };
  }
  return [...renamed.values()];
}

/**
 * A block added while another is selected follows it: the selected block's first exit with
 * nowhere to go points at the new one. False when every exit already goes somewhere.
 */
export function connectFirstFree(
  def: FlowDefinition,
  fromId: string,
  toId: string,
): boolean {
  const from = def.nodes.find((n) => n.id === fromId);
  if (!from || fromId === toId) return false;
  const free = exitsOf(from).find((e) => !e.get(from) && !e.optional);
  if (!free) return false;
  free.set(from, toId);
  return true;
}

/** The link under the pointer: its × shows (FlowEdge). */
export const hoveredEdge = ref<string | null>(null);

/** The drag's data type, read by the canvas when a block from the panel is dropped on it. */
export const BLOCK_DRAG_TYPE = "application/x-donetalk-block";

/** The block panel's groups, in the order a flow is usually built. */
export const KIND_GROUPS: Array<{ id: string; kinds: Kind[] }> = [
  { id: "messages", kinds: ["message", "template", "menu", "input"] },
  {
    id: "ai",
    kinds: ["ai_route", "ai_condition", "ai_tags", "ai_fields", "assistant"],
  },
  {
    id: "logic",
    kinds: ["condition", "split", "repeat", "wait", "business_hours"],
  },
  { id: "contact", kinds: ["tag", "set_field", "set_variable", "optout"] },
  {
    id: "service",
    kinds: ["transfer", "schedule", "close", "goto_flow", "end"],
  },
  {
    id: "integrations",
    kinds: ["http_request", "script", "event", "exception"],
  },
];

/** What "Desfazer" puts back after a bulk change: the nodes, their places and the exception path. */
export function snapshotFlow(def: FlowDefinition): string {
  return JSON.stringify({
    nodes: def.nodes,
    layout: def.layout ?? {},
    exception: def.exception,
    inactivitySeconds: def.inactivitySeconds,
    timeoutNext: def.timeoutNext,
  });
}
export function restoreFlow(def: FlowDefinition, snapshot: string): void {
  const s = JSON.parse(snapshot) as Pick<
    FlowDefinition,
    "nodes" | "layout" | "exception" | "inactivitySeconds" | "timeoutNext"
  >;
  def.nodes = s.nodes;
  def.layout = s.layout;
  for (const key of ["exception", "inactivitySeconds", "timeoutNext"] as const)
    if (s[key] !== undefined)
      (def as unknown as Record<string, unknown>)[key] = s[key];
    else delete def[key];
}

export interface EditorState {
  def: FlowDefinition;
  selectedId: string | null;
  dirty: boolean;
}

export function createEditor(def: FlowDefinition): EditorState {
  const copy = JSON.parse(JSON.stringify(def)) as FlowDefinition;
  copy.layout = autoLayout(copy);
  return reactive({ def: copy, selectedId: null, dirty: false });
}

export function removeNode(def: FlowDefinition, id: string): void {
  if (id === def.start) return;
  def.nodes = def.nodes.filter((n) => n.id !== id);
  for (const n of def.nodes)
    for (const e of exitsOf(n)) if (e.get(n) === id) e.set(n, undefined);
  if (def.exception === id) delete def.exception;
  if (def.layout) delete def.layout[id];
}

export function danglingExits(
  def: FlowDefinition,
): Array<{ nodeId: string; handle: string }> {
  const ids = new Set(def.nodes.map((n) => n.id));
  const out: Array<{ nodeId: string; handle: string }> = [];
  for (const n of def.nodes)
    for (const e of exitsOf(n)) {
      const target = e.get(n);
      if ((!target && !e.optional) || (target && !ids.has(target)))
        out.push({ nodeId: n.id, handle: e.handle });
    }
  return out;
}
